import { isTruthy, useThemeStore } from "@theprelude/core";
import {
  BuiltInEdge,
  Edge,
  EdgeTypes,
  FitViewOptions,
  NodeTypes,
  Panel,
  ReactFlow,
  useNodesState,
  useReactFlow,
} from "@xyflow/react";
import Elk, { ElkNode } from "elkjs/lib/elk.bundled";
import { Dispatch, SetStateAction, Suspense, useEffect, useMemo } from "react";
import type { IsNever } from "type-fest";
import { DataPoint } from "@/queries/control-monitor";
import { ControlMonitorFilters } from "./control-monitor-filters";
import { useControlMonitorNavigation } from "./control-monitor-navigation";
import { AnimatedSmoothStepEdge } from "./edges/animated-smooth-step-edge";
import { BucketNode, BucketNodeType } from "./nodes/bucket-node";
import { ComingSoonNode, ComingSoonNodeType } from "./nodes/coming-soon-node";
import {
  ConnectedControlNode,
  ConnectedControlNodeType,
} from "./nodes/connected-control-node";
import {
  ControlCategoryFailureNode,
  ControlCategoryFailureNodeType,
} from "./nodes/control-category-failure-node";
import {
  ControlCategoryNode,
  ControlCategoryNodeType,
} from "./nodes/control-category-node";
import {
  ControlFailureNode,
  ControlFailureNodeType,
} from "./nodes/control-failure-node";
import {
  DisconnectedControlNode,
  DisconnectedControlNodeType,
} from "./nodes/disconnected-control-node";
import { NoControlsNode, NoControlsNodeType } from "./nodes/no-controls-node";
import { RootNode, RootNodeType } from "./nodes/root-node";
import {
  LoadingThreatButton,
  LoadingThreatCard,
  ThreatViewCard,
} from "./panels/threat-view-card";
import { Toolbar } from "./panels/toolbar";

import "@xyflow/react/dist/style.css";
import "./react-flow-overrides.css";

export type DataPointNode =
  | RootNodeType
  | BucketNodeType
  | ControlCategoryNodeType
  | NoControlsNodeType
  | ConnectedControlNodeType
  | DisconnectedControlNodeType
  | ControlCategoryFailureNodeType
  | ControlFailureNodeType
  | ComingSoonNodeType;

true satisfies IsNever<Exclude<DataPoint["type"], DataPointNode["type"]>>;

const nodeTypes = {
  root: RootNode,
  bucket: BucketNode,
  "coming-soon": ComingSoonNode,
  "connected-control": ConnectedControlNode,
  "control-category-failure": ControlCategoryFailureNode,
  "control-failure": ControlFailureNode,
  "control-category": ControlCategoryNode,
  "disconnected-control": DisconnectedControlNode,
  "no-controls": NoControlsNode,
} satisfies NodeTypes & Record<DataPoint["type"], unknown>;

const edgeTypes = {
  animatedsmoothstep: AnimatedSmoothStepEdge,
} satisfies EdgeTypes;

type Size = { width: number; height: number };

const nodeSize: Record<DataPoint["type"], Size> = {
  root: { width: 240, height: 200 },
  bucket: { width: 240, height: 64 },
  "coming-soon": { width: 240, height: 64 },
  "connected-control": { width: 240, height: 64 },
  "control-category": { width: 240, height: 64 },
  "control-category-failure": { width: 240, height: 64 },
  "control-failure": { width: 240, height: 64 },
  "disconnected-control": { width: 240, height: 64 },
  "no-controls": { width: 320, height: 280 },
};

const updateLayout = async ({
  data,
  edges,
  path,
  setNodes,
}: {
  data: DataPoint[];
  edges: Edge[];
  path: string[];
  setNodes: Dispatch<SetStateAction<DataPointNode[]>>;
}): Promise<void> => {
  const nodes = getNodes({ data, path, edges }).map((node) => ({
    ...node,
    ...nodeSize[node.data.type],
  }));

  const graph: ElkNode = {
    id: "root",
    layoutOptions: {
      "elk.algorithm": "mrtree",
      "elk.direction": "RIGHT",
      "elk.spacing.nodeNode": "50",
    },
    children: nodes,
    edges: edges
      .filter((edge) => nodes.find((n) => n.id === edge.target))
      .map((edge) => ({
        ...edge,
        sources: [edge.source],
        targets: [edge.target],
      })),
  };

  const { children } = await new Elk().layout(graph);

  const positionedNodes: DataPointNode[] = (children ?? [])
    .map((elkNode) => {
      const node = nodes.find((n) => n.id === elkNode.id);
      if (!node) {
        return undefined;
      }
      return { ...node, position: { x: elkNode.x ?? 0, y: elkNode.y ?? 0 } };
    })
    .filter(isTruthy);

  // Ensure the root is always at (0, 0) to prevent graph "jumping" when expanded/collapsed.
  const root = positionedNodes[0];
  const orientatedNodes = root
    ? positionedNodes.map(({ position: { x, y }, ...node }) => ({
        ...node,
        position: { x: x - root.position.x, y: y - root.position.y },
      }))
    : [];

  setNodes((prev) => {
    const unseenNodes = orientatedNodes.filter(
      (node) => !prev.some((n) => n.id === node.id),
    );

    const updatedNodes = prev
      .map(
        (node) =>
          ({
            ...node,
            ...orientatedNodes.find((b) => b.id === node.id),
          }) as DataPointNode,
      )
      .filter((node) => orientatedNodes.some((n) => n.id === node.id));

    return [...updatedNodes, ...unseenNodes];
  });
};

const getNodes = ({
  data,
  path,
  edges,
}: {
  data: DataPoint[];
  path: string[];
  edges: Edge[];
}): DataPointNode[] => {
  return data.reduce((nodes: DataPointNode[], item) => {
    const { id, type, children } = item;
    const position = { x: 0, y: 0 };

    nodes.push({
      type,
      id,
      data: { ...item, type },
      position,
    } as DataPointNode);

    if (id === path[0]) {
      nodes.push(...getNodes({ data: children, path: path.slice(1), edges }));
    }

    return nodes;
  }, []);
};

const getEdges = ({ data }: { data: DataPoint[] }): Edge[] => {
  return data.reduce((edges: Edge[], item) => {
    edges.push(
      ...item.children.map((child): BuiltInEdge | AnimatedSmoothStepEdge => ({
        id: `${item.id}->${child.id}`,
        type: "animatedsmoothstep",
        source: item.id,
        target: child.id,
        pathOptions: {
          borderRadius: 20,
          pathLength: 100,
        },
      })),
      ...getEdges({ data: item.children }),
    );
    return edges;
  }, []);
};

const initialFitViewOptions: FitViewOptions = { maxZoom: 1 };
const fitViewOptions: FitViewOptions = {
  ...initialFitViewOptions,
  duration: 500,
};

const useNodes = (data: DataPoint[], path: string[]) => {
  const edges = useMemo(() => getEdges({ data }), [data]);

  const [nodes, setNodes, onNodesChange] = useNodesState<DataPointNode>([]);

  useEffect(() => {
    void updateLayout({ data, edges, path, setNodes });
  }, [data, edges, path, setNodes]);

  return { edges, nodes, onNodesChange };
};

export function Canvas({
  data,
  threat,
  className,
}: {
  data: DataPoint[];
  threat: string | undefined;
  className?: string;
}) {
  const { path, view } = useControlMonitorNavigation();
  const { fitView } = useReactFlow();
  const { edges, nodes, onNodesChange } = useNodes(data, path);
  const theme = useThemeStore((state) => state.theme ?? "dark");

  useEffect(() => {
    const handleResize = () => {
      void fitView(fitViewOptions);
    };

    window.addEventListener("resize", handleResize);

    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, [fitView, nodes]);

  return (
    <div className={className}>
      <ReactFlow
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        fitView
        fitViewOptions={initialFitViewOptions}
        nodesConnectable={false}
        nodesDraggable={false}
        nodesFocusable={false}
        edgesFocusable={false}
        edgesReconnectable={false}
        elementsSelectable={false}
        proOptions={{ hideAttribution: true }}
        maxZoom={3}
        minZoom={0.5}
        onNodeClick={(e, node) => {
          if (node.data.enabled) {
            void view(node.data);
          }
        }}
        colorMode={theme}
      >
        <Panel position="top-center">
          <Toolbar nodes={nodes} />
        </Panel>
        <Panel position="top-left">
          <Suspense
            fallback={threat ? <LoadingThreatCard /> : <LoadingThreatButton />}
          >
            <ThreatViewCard />
          </Suspense>
        </Panel>

        <Panel position="top-right">
          <ControlMonitorFilters />
        </Panel>
      </ReactFlow>
    </div>
  );
}
